'use client';

import { useEffect, useState } from 'react';
import type { GapPeer } from '@/lib/grants-embed/lookups';

const STEP_MS = 160;
const HOLD_MS = 700;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The wait, made visible. While the comparison runs we name what we're doing;
 * once it returns, the real matched districts appear one by one before the
 * results take over. Skipped for reduced-motion visitors.
 */
export function ComparisonReveal({
  subjectName,
  peers,
  onDone,
}: {
  subjectName: string;
  /** null while the lead request is still in flight. */
  peers: GapPeer[] | null;
  onDone: () => void;
}) {
  const [shown, setShown] = useState(0);
  // Keyed by id: districts in different states often share a name ("Washington Local").
  const shownPeers = (peers ?? []).slice(0, 10);
  const names = shownPeers.map((p) => p.districtName);

  useEffect(() => {
    if (!peers) return;
    if (prefersReducedMotion() || names.length === 0) {
      onDone();
      return;
    }
    if (shown < names.length) {
      const t = setTimeout(() => setShown((n) => n + 1), STEP_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(onDone, HOLD_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peers, shown]);

  return (
    <div className="rounded-md bg-[var(--g-ink)] px-6 py-10 text-white sm:px-10" aria-live="polite">
      <p className="g-display text-2xl font-bold sm:text-3xl">
        {peers ? `${names.length} districts like ${subjectName}` : `Finding districts like ${subjectName}`}
      </p>
      <p className="mt-1 text-sm text-white/70">
        {peers ? 'Comparing their federal awards with yours.' : 'Matching on size, poverty, spending and locale.'}
      </p>
      <ol className="mt-6 grid min-h-40 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
        {shownPeers.slice(0, shown).map((p) => (
          <li key={p.districtId} className="g-rise flex items-center gap-2">
            <span aria-hidden className="size-2 rounded-full bg-[var(--g-accent)]" />
            {p.districtName}
          </li>
        ))}
        {!peers && (
          <li className="flex items-center gap-2 text-white/60">
            <span aria-hidden className="size-2 animate-pulse rounded-full bg-white/60" />
            Searching every U.S. school district
          </li>
        )}
      </ol>
    </div>
  );
}
